import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { LayoutDashboard, FileCheck, Wallet, MessageCircle, MoreHorizontal } from 'lucide-react';

/**
 * Bottom-fixed tab bar for the Customer Portal on mobile. Hidden on ≥md.
 *
 * Displays the 4 most-used portal routes + a "More" link that opens the
 * existing left sidebar drawer (handled by DashboardLayout's mobileOpen).
 */
export default function MobileBottomNav({ role = 'company', onMoreClick }) {
  const location = useLocation();

  // While this bar is rendered, any `fixed bottom-X` widgets (chat bubble,
  // cookie banner, toasts) need to shift up. We expose the bar's height as
  // a CSS variable on <body>; globals.css applies it via
  // `bottom: calc(... + var(--mobile-nav-offset))` where appropriate.
  React.useEffect(() => {
    if (role !== 'company') return undefined;
    document.body.style.setProperty('--mobile-nav-offset', '68px');
    return () => { document.body.style.removeProperty('--mobile-nav-offset'); };
  }, [role]);

  if (role !== 'company') return null;

  const items = [
    { label: 'Dashboard',   icon: LayoutDashboard, path: '/portal' },
    { label: 'Zertifikate', icon: FileCheck,       path: '/portal/certificates' },
    { label: 'Guthaben',    icon: Wallet,          path: '/portal/wallet' },
    { label: 'Support',     icon: MessageCircle,   path: '/portal/support' },
  ];

  return (
    <nav
      data-testid="mobile-bottom-nav"
      className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-[#E4E4E7] px-2 py-1.5"
      style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}
    >
      <div className="flex items-stretch justify-around">
        {items.map((it) => {
          const Icon = it.icon;
          const isActive = location.pathname === it.path
            || (it.path !== '/portal' && location.pathname.startsWith(it.path));
          return (
            <NavLink
              key={it.path}
              to={it.path}
              className={`flex-1 flex flex-col items-center gap-0.5 py-1.5 rounded-lg tap-fast transition-colors ${isActive ? 'text-[#0F8A5F]' : 'text-[#71717A]'}`}
              data-testid={`mobile-nav-${it.label.toLowerCase()}`}
            >
              <Icon className={`w-5 h-5 ${isActive ? 'text-[#0F8A5F]' : ''}`} strokeWidth={isActive ? 2.4 : 2} />
              <span className="text-[10px] font-semibold">{it.label}</span>
            </NavLink>
          );
        })}
        <button
          onClick={onMoreClick}
          className="flex-1 flex flex-col items-center gap-0.5 py-1.5 rounded-lg tap-fast text-[#71717A]"
          data-testid="mobile-nav-more"
        >
          <MoreHorizontal className="w-5 h-5" />
          <span className="text-[10px] font-semibold">Mehr</span>
        </button>
      </div>
    </nav>
  );
}
